/**
 * Motor de agregación: convierte filas planas en series listas para Recharts.
 *
 * aggregateRows(rows, {
 *   dimension,            // campo del eje X / agrupación
 *   dateGranularity,      // 'day' | 'month' | 'quarter' | 'year' (si la dimensión es fecha)
 *   breakdown,            // campo opcional para desglosar en varias series
 *   metric: { agg, field },
 *   sort,                 // 'valueDesc' | 'valueAsc' | 'labelAsc' | 'labelDesc' | 'natural'
 *   limit,                // top N (0 = sin límite)
 *   groupOthers,          // agrupar el resto en "Otros"
 * })
 */

import { getField } from './fieldsNew'

export const AGGREGATIONS = [
  { value: 'sum', label: 'Suma', needsField: true, symbol: 'Σ' },
  { value: 'avg', label: 'Media', needsField: true, symbol: 'x̄' },
  { value: 'min', label: 'Mínimo', needsField: true, symbol: '↓' },
  { value: 'max', label: 'Máximo', needsField: true, symbol: '↑' },
  { value: 'count', label: 'Nº de registros', needsField: false, symbol: '#' },
  { value: 'countDistinct', label: 'Valores distintos', needsField: true, symbol: '#≠' },
]

export const DATE_GRANULARITIES = [
  { value: 'day', label: 'Día' },
  { value: 'month', label: 'Mes' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'year', label: 'Año' },
]

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

export function aggregationMeta(value) {
  return AGGREGATIONS.find((item) => item.value === value) || AGGREGATIONS[0]
}

/** Etiqueta legible de la métrica: "Suma de Peso". */
export function metricLabel(metric) {
  const agg = aggregationMeta(metric?.agg)
  if (!agg.needsField) return agg.label
  const field = getField(metric?.field)
  return `${agg.label} de ${field ? field.label : '—'}`
}

/** Unidad y decimales resultantes de la métrica. */
export function metricFormat(metric) {
  const agg = aggregationMeta(metric?.agg)
  const field = getField(metric?.field)
  if (!agg.needsField || agg.value === 'countDistinct' || agg.value === 'count') {
    return { unit: '', decimals: 0 }
  }
  return { unit: field?.unit || '', decimals: field?.decimals ?? 0 }
}

function dateBucket(value, granularity) {
  const iso = String(value).slice(0, 10)
  const [year, month, day] = iso.split('-')
  if (!year) return { key: '—', label: '—' }
  switch (granularity) {
    case 'year':
      return { key: year, label: year }
    case 'quarter': {
      const quarter = Math.floor((Number(month) - 1) / 3) + 1
      return { key: `${year}-Q${quarter}`, label: `T${quarter} ${year}` }
    }
    case 'day':
      return { key: iso, label: `${day}/${month}/${year.slice(2)}` }
    case 'month':
    default:
      return { key: `${year}-${month}`, label: `${MONTHS[Number(month) - 1]} ${year.slice(2)}` }
  }
}

/** Clave + etiqueta del bucket de una fila para una dimensión dada. */
export function bucketOf(row, dimensionKey, granularity) {
  const field = getField(dimensionKey)
  const raw = row[dimensionKey]
  if (raw === null || raw === undefined || raw === '') return { key: '∅', label: 'Sin valor' }
  if (field?.type === 'date') return dateBucket(raw, granularity)
  if (field?.type === 'boolean') return { key: raw ? 'si' : 'no', label: raw ? 'Sí' : 'No' }
  return { key: String(raw), label: String(raw) }
}

function createBucket() {
  return { sum: 0, count: 0, min: Infinity, max: -Infinity, distinct: new Set() }
}

function push(bucket, row, metric) {
  bucket.count += 1
  if (!metric?.field) return
  const raw = row[metric.field]
  if (metric.agg === 'countDistinct') {
    if (raw !== null && raw !== undefined && raw !== '') bucket.distinct.add(String(raw))
    return
  }
  const num = Number(raw)
  if (!Number.isFinite(num)) return
  bucket.sum += num
  bucket.min = Math.min(bucket.min, num)
  bucket.max = Math.max(bucket.max, num)
  bucket.numeric = (bucket.numeric || 0) + 1
}

function resolve(bucket, metric) {
  switch (metric?.agg) {
    case 'count':
      return bucket.count
    case 'countDistinct':
      return bucket.distinct.size
    case 'avg':
      return bucket.numeric ? bucket.sum / bucket.numeric : 0
    case 'min':
      return bucket.min === Infinity ? 0 : bucket.min
    case 'max':
      return bucket.max === -Infinity ? 0 : bucket.max
    case 'sum':
    default:
      return bucket.sum
  }
}

/** Métrica global sobre un conjunto de filas (KPIs). */
export function aggregateValue(rows, metric) {
  const bucket = createBucket()
  rows.forEach((row) => push(bucket, row, metric))
  return resolve(bucket, metric)
}

export function aggregateRows(rows, config) {
  const {
    dimension,
    dateGranularity = 'month',
    breakdown = '',
    metric = { agg: 'sum', field: 'peso' },
    secondMetric = null,
    sort = 'valueDesc',
    limit = 0,
    groupOthers = false,
  } = config || {}

  const dimensionField = getField(dimension)
  const useMultiMetric = secondMetric && !breakdown

  // --- Multi-metric mode: two independent series from two metrics ------------
  if (useMultiMetric) {
    const m1 = metricLabel(metric)
    const m2 = metricLabel(secondMetric)
    const seriesNames = [m1, m2]
    const metricMap = { [m1]: metric, [m2]: secondMetric }

    const dimBuckets = new Map()

    rows.forEach((row) => {
      const { key, label } = bucketOf(row, dimension, dateGranularity)
      if (!dimBuckets.has(key)) {
        dimBuckets.set(key, { key, label, b1: createBucket(), b2: createBucket() })
      }
      const entry = dimBuckets.get(key)
      push(entry.b1, row, metric)
      push(entry.b2, row, secondMetric)
    })

    let data = Array.from(dimBuckets.values()).map((entry) => {
      const v1 = Number(resolve(entry.b1, metric).toFixed(4))
      const v2 = Number(resolve(entry.b2, secondMetric).toFixed(4))
      return { name: entry.label, __key: entry.key, [m1]: v1, [m2]: v2, __total: v1 }
    })

    const effectiveSort = sort === 'natural' && dimensionField?.type !== 'date' ? 'labelAsc' : sort
    data.sort((a, b) => {
      switch (effectiveSort) {
        case 'valueAsc':
          return a.__total - b.__total
        case 'labelAsc':
          return String(a.__key).localeCompare(String(b.__key), 'es', { numeric: true })
        case 'labelDesc':
          return String(b.__key).localeCompare(String(a.__key), 'es', { numeric: true })
        case 'natural':
          return String(a.__key).localeCompare(String(b.__key), 'es', { numeric: true })
        case 'valueDesc':
        default:
          return b.__total - a.__total
      }
    })

    if (limit > 0 && data.length > limit) {
      const visible = data.slice(0, limit)
      if (groupOthers) {
        const rest = data.slice(limit)
        const others = { name: 'Otros', __key: '__others', __total: 0 }
        seriesNames.forEach((sn) => {
          others[sn] = Number(rest.reduce((acc, item) => acc + (item[sn] || 0), 0).toFixed(4))
        })
        others.__total = others[m1]
        visible.push(others)
      }
      data = visible
    }

    return {
      data,
      series: seriesNames,
      total: data.reduce((acc, item) => acc + item.__total, 0),
      format: metricFormat(metric),
      label: metricLabel(metric),
      metricMap,
    }
  }

  // --- Single-metric mode (breakdown or plain) ------------------------------
  const buckets = new Map()
  const seriesTotals = new Map()

  rows.forEach((row) => {
    const { key, label } = bucketOf(row, dimension, dateGranularity)
    const seriesName = breakdown
      ? bucketOf(row, breakdown, dateGranularity).label
      : metricLabel(metric)

    if (!buckets.has(key)) buckets.set(key, { key, label, series: new Map() })
    const entry = buckets.get(key)
    if (!entry.series.has(seriesName)) entry.series.set(seriesName, createBucket())
    push(entry.series.get(seriesName), row, metric)

    if (!seriesTotals.has(seriesName)) seriesTotals.set(seriesName, createBucket())
    push(seriesTotals.get(seriesName), row, metric)
  })

  let data = Array.from(buckets.values()).map((entry) => {
    const item = { name: entry.label, __key: entry.key, __total: 0 }
    entry.series.forEach((bucket, seriesName) => {
      const value = Number(resolve(bucket, metric).toFixed(4))
      item[seriesName] = value
      item.__total += value
    })
    return item
  })

  const series = Array.from(seriesTotals.entries())
    .sort((a, b) => resolve(b[1], metric) - resolve(a[1], metric))
    .map(([name]) => name)

  const effectiveSort = sort === 'natural' && dimensionField?.type !== 'date' ? 'labelAsc' : sort
  data.sort((a, b) => {
    switch (effectiveSort) {
      case 'valueAsc':
        return a.__total - b.__total
      case 'labelAsc':
        return String(a.__key).localeCompare(String(b.__key), 'es', { numeric: true })
      case 'labelDesc':
        return String(b.__key).localeCompare(String(a.__key), 'es', { numeric: true })
      case 'natural':
        return String(a.__key).localeCompare(String(b.__key), 'es', { numeric: true })
      case 'valueDesc':
      default:
        return b.__total - a.__total
    }
  })

  if (limit > 0 && data.length > limit) {
    const visible = data.slice(0, limit)
    if (groupOthers) {
      const rest = data.slice(limit)
      const others = { name: 'Otros', __key: '__others', __total: 0 }
      series.forEach((seriesName) => {
        others[seriesName] = Number(
          rest.reduce((acc, item) => acc + (item[seriesName] || 0), 0).toFixed(4)
        )
        others.__total += others[seriesName]
      })
      visible.push(others)
    }
    data = visible
  }

  return {
    data,
    series: series.length ? series : [metricLabel(metric)],
    total: data.reduce((acc, item) => acc + item.__total, 0),
    format: metricFormat(metric),
    label: metricLabel(metric),
  }
}
